import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	EditorCommand,
	ExtractInjectionAPI,
	PMPluginFactoryParams,
} from '@atlaskit/editor-common/types';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import { openPostStreamReviewModal } from '../../editor-actions/actions';
import type { ErrorAIEvent } from '../../editor-actions/types';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { configItemUsesRovo } from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import { isNewAifcExperienceEnabled } from '../../ui/utils/new-aifc-experience-enabled';
import { createStreamFlowPacer } from '../../ui/utils/request/stream-flow-pacer';
import { createStreaming } from '../../ui/utils/streaming';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pure-pm-in-editor-direct-streaming/pluginKey';

import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import {
	streamADFResponse,
	completeADFStream,
	errorADFStream,
	resetSessionState,
	updateIdMap,
	updateTraceIds,
} from './commands';
import {
	AI_CONTENT_SEGMENT_DATA,
	type AIContentSegmentData,
	AI_FINAL_STREAMING_TRANSFORMATION_DATA,
	type AIFinalStreamingTransformationData,
	AI_ORIGINAL_NODE_DATA,
	type AIOriginalNodeData,
	CAPTURE_ABORTED_TOOL_CALL_POSITIONS,
	type CaptureAbortedToolCallPositionsData,
} from './meta-keys';
import { reducer } from './reducer';
import type {
	AIContentPositionEntry,
	SessionContextPluginState,
	SessionContextAction,
} from './types';

export const initialState: SessionContextPluginState = {
	adfResponse: undefined,
	status: 'idle',
	responseVersion: 0,
	streamPaused: false,
	hasUserScrolled: false,
	// [CCI-15904] New AIFC Editor Experience — tracks AI-produced top-level change
	// ranges across a single session for the Post Stream Review toolbar.
	aiContentPositions: [],
	// Positions of tool calls aborted by the queue timeout, keyed by id. Remapped
	// on every doc change and consumed on resume.
	abortedToolCallPositions: {},
	// Id of a tool call awaiting final-position capture in the next
	// buildCommitTransaction; cleared once captured.
	pendingForceAbortedToolCallId: undefined,
};

export interface AISessionContextPluginOptions {
	/**
	 * [CCI-15904] The global AI opt-in state. Required by `openPostStreamReviewModal`
	 * → `openAIModal` to gate the Post Stream Review modal behind FedRAMP / opt-in
	 * checks when auto-opening it after a quick-action stream completes.
	 */
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: PMPluginFactoryParams['dispatch'];
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
	getMentionNameDetails: GetMentionNameDetails;
}

/**
 * This plugin stores the core business logic state for the AI session.
 * It manages the session lifecycle including:
 * - State transitions (idle → streaming → complete)
 * - Streaming initiation for non-orchestrator prompts
 * - Bridging stream events back to plugin state
 *
 * The view.update() method detects when streaming should start (based on decoration state)
 * and initiates the network request, bridging responses back to this plugin's state.
 * The pure-pm plugin then reacts to state changes to update the document.
 */
export const createAISessionContextPlugin = (
	options: AISessionContextPluginOptions,
): SafePlugin => {
	const { aiGlobalOptIn, api, dispatch, editorPluginAIProvider, getIntl, getMentionNameDetails } =
		options;

	return new SafePlugin<SessionContextPluginState>({
		key: aiSessionContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: SessionContextPluginState) => {
				let nextState: SessionContextPluginState = pluginState;

				// [CCI-15904] Remap stored AI content positions through tr.mapping on every
				// doc-changing transaction so positions remain accurate across collab edits,
				// undo/redo, and subsequent AI tool-call commits within the same session.
				if (tr.docChanged && nextState.aiContentPositions?.length) {
					const remapped = nextState.aiContentPositions.map((entry: AIContentPositionEntry) => {
						const mappedStart = tr.mapping.map(entry.startPos, 1);
						const mappedEnd = tr.mapping.map(entry.endPos, -1);
						// The start/end biases (start after preceding inserts, end before
						// following inserts) can cross when content is re-inserted exactly
						// at the stored range — e.g. undoing a `remove` segment restores the
						// removed content at that point, pushing `startPos` past `endPos` and
						// leaving them swapped. Normalise so the lower value is always the
						// start and the higher value the end, otherwise a subsequent redo
						// resolves an inverted range and applies at the wrong position.
						return {
							...entry,
							startPos: Math.min(mappedStart, mappedEnd),
							endPos: Math.max(mappedStart, mappedEnd),
						};
					});
					nextState = { ...nextState, aiContentPositions: remapped };
				}

				// Remap the fine step-through segments with the SAME bias
				// handling as `aiContentPositions`, so a per-segment undo/redo (which
				// changes the doc) keeps every OTHER fine segment's range accurate. This
				// is what makes per-segment undo/redo correct when one coarse commit was
				// split into multiple fine segments.
				if (tr.docChanged && nextState.streamReviewSegments?.length) {
					const remapped = nextState.streamReviewSegments.map((entry: AIContentPositionEntry) => {
						const mappedStart = tr.mapping.map(entry.startPos, 1);
						const mappedEnd = tr.mapping.map(entry.endPos, -1);
						return {
							...entry,
							startPos: Math.min(mappedStart, mappedEnd),
							endPos: Math.max(mappedStart, mappedEnd),
						};
					});
					nextState = { ...nextState, streamReviewSegments: remapped };
				}

				// Remap stored aborted-tool-call positions on every doc change so a
				// resumed tool call re-applies where it was originally working,
				// accounting for content other tool calls inserted/removed after it was
				// aborted. Same bias as aiContentPositions: start after preceding
				// inserts (1), end before following inserts (-1).
				if (tr.docChanged && nextState.abortedToolCallPositions) {
					const ids = Object.keys(nextState.abortedToolCallPositions);
					if (ids.length) {
						const remappedAborted: NonNullable<
							SessionContextPluginState['abortedToolCallPositions']
						> = {};
						for (const id of ids) {
							const entry = nextState.abortedToolCallPositions[id];
							remappedAborted[id] = {
								startPos: tr.mapping.map(entry.startPos, 1),
								aiContentEndPos: tr.mapping.map(entry.aiContentEndPos, -1),
							};
						}
						nextState = { ...nextState, abortedToolCallPositions: remappedAborted };
					}
				}

				// [CCI-15904] Append a new AI content position entry when the transaction
				// carries the AI_FINAL_STREAMING_TRANSFORMATION_DATA meta.
				// Set by `buildCommitTransaction` when the New AIFC Editor Experience gate is ON.
				//
				// We intentionally do NOT gate this on `nextState.sessionId`: for quick actions
				// (Improve writing, Change tone, etc.) `startSession` is never called, so
				// `sessionId` is `undefined` even though `buildCommitTransaction` has fired and
				// `aiContentPositions` must still be populated for the Post Stream Review modal.
				const finalStreamData = tr.getMeta(AI_FINAL_STREAMING_TRANSFORMATION_DATA) as
					| AIFinalStreamingTransformationData
					| undefined;
				if (finalStreamData) {
					nextState = {
						...nextState,
						aiContentPositions: [
							...(nextState.aiContentPositions ?? []),
							{
								startPos: finalStreamData.positions.mappedStartPos,
								endPos: finalStreamData.positions.mappedToPos,
								// [CCI-17658] Seed the two-slice history [original, first AI pass]
								// with the AI pass applied. A per-segment rework appends further
								// slices; `appliedIndex` selects the applied one.
								slices: [finalStreamData.originalUserContentSlice, finalStreamData.newSlice],
								appliedIndex: 1,
								// optional; undefined for streamable commits.
								kind: finalStreamData.kind,
							},
						],
						// Clear the recorded `replaceNode` node (already read at mount) so
						// it can't leak into a later command's mount.
						replaceNodeOriginalNode: undefined,
					};
				}

				// Append coarse AI content position entries recorded by a
				// NON-STREAMABLE command handler (removeNode, setAttrs, move, wrap, ...).
				// These commands mutate the doc directly and never reach
				// `buildCommitTransaction`, so they stamp AI_CONTENT_SEGMENT_DATA instead.
				// Consumed identically to the streamable path so the Post Stream Review
				// modal can navigate / undo / redo them.
				const segmentData = tr.getMeta(AI_CONTENT_SEGMENT_DATA) as AIContentSegmentData | undefined;
				if (segmentData && segmentData.segments.length > 0) {
					nextState = {
						...nextState,
						aiContentPositions: [
							...(nextState.aiContentPositions ?? []),
							...segmentData.segments.map((segment) => ({
								startPos: segment.startPos,
								endPos: segment.endPos,
								// [CCI-17658] Two-slice history [original, AI pass], AI pass applied.
								slices: [segment.originalUserContentSlice, segment.newSlice],
								appliedIndex: 1,
								kind: segment.kind,
							})),
						],
					};
				}

				// Store the node a `replaceNode` command recorded so
				// `buildMountTransaction` can rebuild an accurate single-node
				// `originalUserContentSlice`. Transient field only — not a reviewable
				// segment, so `aiContentPositions` is untouched.
				const originalNodeData = tr.getMeta(AI_ORIGINAL_NODE_DATA) as
					| AIOriginalNodeData
					| undefined;
				if (originalNodeData) {
					nextState = {
						...nextState,
						replaceNodeOriginalNode: originalNodeData.originalNode,
					};
				}

				// Persist a force-finished tool call's final positions from the meta
				// stamped by `buildCommitTransaction`. Stored by id (then remapped on
				// every later doc change above); captured exactly once.
				const capturedAbortedPositions = tr.getMeta(CAPTURE_ABORTED_TOOL_CALL_POSITIONS) as
					| CaptureAbortedToolCallPositionsData
					| undefined;
				if (capturedAbortedPositions) {
					nextState = {
						...nextState,
						abortedToolCallPositions: {
							...nextState.abortedToolCallPositions,
							[capturedAbortedPositions.toolCallId]: {
								startPos: capturedAbortedPositions.startPos,
								aiContentEndPos: capturedAbortedPositions.aiContentEndPos,
							},
						},
						pendingForceAbortedToolCallId: undefined,
					};
				}

				// Existing behaviour — process dispatched plugin-key actions.
				const meta = tr.getMeta(aiSessionContextPluginKey);
				if (meta) {
					nextState = reducer({
						state: nextState,
						action: meta as SessionContextAction,
					});
				}

				if (nextState !== pluginState) {
					dispatch(aiSessionContextPluginKey, nextState);
				}
				return nextState;
			},
		},
		/**
		 * When all prompts are migrated to use the orchestrator, this entire view can be removed.
		 */
		view: () => {
			const streamFlowPacer = createStreamFlowPacer();
			let abortStreaming: (() => void) | undefined;
			let hasStartedStreaming = false;
			// Streaming setup is async; if cancel/reset happens before cleanup is available,
			// this "sticky" flag ensures we abort immediately once cleanup resolves.
			// It also guards against late/buffered events reactivating plugin state after cancel/reset.
			let cancelled = false;

			const cancelStreaming = () => {
				cancelled = true;
				hasStartedStreaming = false;
				if (abortStreaming) {
					abortStreaming();
					abortStreaming = undefined;
				}
			};

			return {
				update(view: EditorView, previousState: EditorState) {
					const sessionState = aiSessionContextPluginKey.getState(view.state);
					const prevSessionState = aiSessionContextPluginKey.getState(previousState);
					const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
					const prevDecorationState = aiExperienceDecorationPluginKey.getState(previousState);

					// Handle cancel request - detect when cancelRequested flag was just set
					const cancelJustRequested =
						sessionState?.cancelRequested && !prevSessionState?.cancelRequested;
					if (cancelJustRequested) {
						cancelStreaming();
					}

					// Reset streaming flag and abort any in-flight request when session ends
					// This handles both explicit cancel and implicit reset
					if (sessionState?.status === 'idle') {
						cancelStreaming();
						return;
					}

					// For the fallback free-gen path: reset session state after finalize completes.
					// The 'complete' event handler defers the reset so buildCommitTransaction can
					// still read markdown/idMap from session state during the finalize flow.
					// Once finalize is done (hasFinalStream: true), it's safe to reset.
					if (sessionState?.status === 'complete' && sessionState?.userInput !== undefined) {
						const streamingState = purePmInEditorDirectStreamingPluginKey.getState(view.state);
						if (streamingState?.hasFinalStream) {
							api?.core?.actions.execute(resetSessionState());
							return;
						}
					}

					// When streamPaused changes, pause or resume the pacer so the discard screen can stop delivery
					if (sessionState?.streamPaused !== prevSessionState?.streamPaused) {
						if (sessionState?.streamPaused) {
							streamFlowPacer.pause();
						} else {
							streamFlowPacer.resume();
						}
					}

					// Detect when we should start streaming:
					// - configItem just became available (decoration state changed)
					// - Not a Rovo/orchestrator prompt
					// - Haven't already started streaming for this session
					const configItemJustSet = decorationState?.configItem && !prevDecorationState?.configItem;
					const shouldStartStream =
						configItemJustSet &&
						decorationState?.positions &&
						!hasStartedStreaming &&
						!configItemUsesRovo(decorationState.configItem?.key);

					// Detect when the fallback free-gen prompt submits user input.
					// This happens when START_FREE_GEN_STREAM action is dispatched from the PromptScreen
					// for products that don't have Rovo/onSubmit (e.g. Jira, Bitbucket).
					const freeGenStreamJustStarted =
						sessionState?.status === 'streaming' &&
						prevSessionState?.status === 'prompt' &&
						sessionState?.userInput !== undefined &&
						!hasStartedStreaming &&
						decorationState?.configItem &&
						decorationState?.positions;

					if (!shouldStartStream && !freeGenStreamJustStarted) {
						return;
					}

					if (!decorationState?.configItem || !decorationState?.positions) {
						return;
					}

					hasStartedStreaming = true;
					cancelled = false;

					// Create the streaming handler
					// Note: Analytics for streaming is handled separately via the AnalyticsFlow context
					// in the React layer. This needs fixing
					const streamingHandler = createStreaming({
						fireAIAnalyticsEvent: () => {},
						streamFlowPacer,
					});

					// Build the request context
					const positions = decorationState.positions;
					const isEmpty = positions[0] === positions[1];
					const selectionType = isEmpty ? 'empty' : 'range';
					const context = {
						aiProvider: editorPluginAIProvider,
						intl: getIntl(),
						editorView: view,
						positions,
						isEmpty,
						selectionType: selectionType as 'empty' | 'range',
						configItem: decorationState.configItem,
						userInput: freeGenStreamJustStarted ? (sessionState?.userInput ?? '') : '',
						analyticsContext: { aiSessionId: '', aiGroupId: '' },
						usePageKnowledge: true,
						getMentionNameDetails,
						responseHistory: { entries: [], positionFromEnd: 0, totalSize: 0, traversals: 0 },
					};

					// Invoke streaming and bridge events to session context
					streamingHandler(context, (event) => {
						if (cancelled) {
							return;
						}
						switch (event.type) {
							case 'stream':
								if (event.adfResponse || event.markdown) {
									api?.core?.actions.execute(
										streamADFResponse({
											type: 'stream',
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										}),
									);
								}
								break;
							case 'complete':
								if (event.adfResponse || event.markdown) {
									api?.core?.actions.execute(
										completeADFStream({
											type: 'complete',
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										}),
									);
								}
								// Free-gen path keeps session state alive for the UI layer to reset
								// after accept/reject.
								if (!freeGenStreamJustStarted) {
									// [CCI-15904] Quick-action path: END_SESSION (retains
									// aiContentPositions) + auto-open Post Stream Review modal.
									// Only valid when this 'complete' event carried content (otherwise
									// stale positions from a prior session could be reused) AND
									// baseGenerate is present (needed by SHOW_MODAL). Otherwise fall
									// back to RESET so positions don't leak into the next session.
									const baseGenerate = editorPluginAIProvider.baseGenerate;
									const hasContent = !!(event.adfResponse || event.markdown);
									if (isNewAifcExperienceEnabled() && baseGenerate && hasContent) {
										// Two microtasks: (1) let END_SESSION commit; (2) let the
										// reducer apply it before reading aiContentPositions.
										void (async () => {
											await Promise.resolve();
											if (view.isDestroyed) {
												return;
											}
											api?.core?.actions.execute(createCommand({ type: 'END_SESSION' }));

											await Promise.resolve();
											if (view.isDestroyed) {
												return;
											}
											const latestSessionState = aiSessionContextPluginKey.getState(view.state);
											if (latestSessionState?.aiContentPositions?.length) {
												openPostStreamReviewModal({
													editorView: view,
													baseGenerate,
													aiOptIn: aiGlobalOptIn,
												});
											}
										})();
									} else {
										api?.core?.actions.execute(resetSessionState());
									}
								}
								break;
							case 'response too similar':
								api?.core?.actions.execute(
									completeADFStream({
										type: 'complete',
										responseTooSimilar: true,
									}),
								);

								break;
							case 'error':
								if (event.errorInfo) {
									api?.core?.actions.execute(errorADFStream(event as ErrorAIEvent));
								}
								break;
							case 'update traceIds':
								if (event.traceIds) {
									api?.core?.actions.execute(updateTraceIds(event.traceIds));
								}
								break;
							case 'update idMap':
								if (event.context?.idMap) {
									api?.core?.actions.execute(updateIdMap(event.context.idMap));
								}
								break;
							default:
								break;
						}
					}).then((cleanup) => {
						if (!cleanup) {
							return;
						}
						// If cancel/reset happened before cleanup was available, abort immediately.
						if (cancelled) {
							cleanup();
							abortStreaming = undefined;
							return;
						}
						abortStreaming = cleanup;
					});
				},

				destroy() {
					abortStreaming?.();
					streamFlowPacer.destroy();
				},
			};
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): SessionContextPluginState | undefined => {
	return aiSessionContextPluginKey.getState(state);
};

export const createCommand = (action: SessionContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiSessionContextPluginKey, action);
	};
};
